import {
    Dispatch,
    SetStateAction,
    useEffect,
    useState,
    useMemo,
    useCallback,
    memo,
} from "react";
import DynamicTagsComponent from "./DynamicTagsComponent";
import { Tag, Popover, Spin } from "antd";
import { TaggingActionTypes, TAGS_MODULE_MAPPING } from "@ts/constants";
import { PiDotsSixVerticalBold } from "react-icons/pi";
import { showToast } from "@ts/modals";
import { addEntityTags } from "@ts/retrieve-data";
import { getTagType, createWorkers, getGridTagsFormat } from "@ts/utils";
import { setUpdateGridData } from "@ts/filters";
import {
    TagsMap,
    SubModule,
    TagWorkerType,
    TagWorkerResult,
    IChannel,
} from "@ts/common-types";
import { usePermissionCheck } from "@hooks/AllCustomHooks";
import { FixedSizeList as List } from "react-window";
import { filterTags as filterTagsFallback } from "@workers/tag.worker";
import { map } from "underscore";
import { useSelector } from "react-redux";
import { useFeatureIsOn } from "@growthbook/growthbook-react";

interface ICampaignTagsDropDown {
    params?: any;
    rowId: string;
    recordId: number | string;
    recordName: string;
    avlTagsConstant: string[];
    setAvlTagsConstant: Dispatch<SetStateAction<string[]>>;
    currentTags: string[];
    activeModule: string;
    isProcessing: boolean;
    avlDataNotInCurrentTags: any[];
}

// Memoize the tag row component
const TagRow = memo(
    ({
        data: { tag, recordType, onTagClick },
        style,
    }: {
        data: {
            tag: string;
            recordType: string;
            onTagClick: (tag: string) => void;
        };
        style: React.CSSProperties;
    }) => {
        const tagValue = tag?.split("##")[1];
        const { tagType, tagFillColor, tagBorderColor } = getTagType(
            tag,
            recordType
        );

        return (
            <div style={style}>
                <div className="d-flex align-items-center">
                    <PiDotsSixVerticalBold
                        className="fs-5 me-1"
                        fill="#9BABB8"
                    />
                    <Tag
                        key={`${tagType}-${tag}`}
                        className={`m-1`}
                        onClick={() => onTagClick(tag)}
                        role="button"
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            borderRadius: "4px",
                            border: `1px solid ${tagBorderColor}`,
                            overflow: "hidden",
                            width: "160px",
                            padding: 0,
                        }}
                    >
                        <span
                            style={{
                                width: 25,
                                justifyContent: "center",
                                alignItems: "center",
                                backgroundColor: `${tagFillColor}`,
                                color: `white`,
                                padding: "0 8px",
                                display: "flex",
                                borderRight: `1px solid ${tagBorderColor}`,
                                flexShrink: 0,
                            }}
                        >
                            {tagType[0]}
                        </span>
                        <span
                            style={{
                                padding: "0 8px",
                                color: `black`,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                flexGrow: 1,
                            }}
                        >
                            {tagValue}
                        </span>
                    </Tag>
                </div>
            </div>
        );
    }
);

const CampaignTagsDropDown = memo(
    ({
        params,
        recordId,
        recordName,
        avlTagsConstant,
        currentTags,
        activeModule,
        isProcessing,
        avlDataNotInCurrentTags,
    }: ICampaignTagsDropDown) => {
        const [isViewModule] = useState(activeModule !== SubModule.KEYWORD);
        const [avlTags, setAvlTags] = useState<string[]>(avlTagsConstant);
        const [tags, setTags] = useState(currentTags || []);
        const [inputValue, setInputValue] = useState("");
        const [filteredTags, setFilteredTags] =
            useState<string[]>(avlTagsConstant);
        const { updateMethod, updatePath, updateKey, isExternalTagging } =
            TAGS_MODULE_MAPPING[activeModule as keyof TagsMap];
        const channel = useSelector((state: IChannel) => state.channel.value);

        const { canAssignTags } = usePermissionCheck();
        
        const campaignTaggingFeatureEnabled = useFeatureIsOn("CAMPAIGN_TAGGING_FEATURE") || false;
        const canEditTags = canAssignTags && campaignTaggingFeatureEnabled;

        useEffect(() => {
            if (inputValue.trim() === "") {
                setFilteredTags(avlTags);
                return;
            }
            let isMounted = true;
            const filterTags = async () => {
                if (window.Worker) {
                    const result: any = await createWorkers("tag", {
                        type: TagWorkerType.FILTER_TAGS,
                        payload: { tags: avlTags, inputValue },
                    });
                    if (
                        isMounted &&
                        result.type === TagWorkerResult.FILTER_TAGS_RESULT
                    ) {
                        setFilteredTags(result.payload);
                    }
                } else {
                    // Fallback logic
                    const filteredTagsData = filterTagsFallback({
                        tags: avlTags,
                        inputValue,
                    });
                    if (isMounted) {
                        setFilteredTags(filteredTagsData);
                    }
                }
            };
            filterTags();
            return () => {
                isMounted = false;
            };
        }, [inputValue, avlTags]);

        useEffect(() => {
            setAvlTags(avlTagsConstant);
        }, [avlTagsConstant]);

        const handleTagsPopoverVisibleChange = useCallback(
            async (visible: boolean) => {
                if (!visible) {
                    const sortedCurTags = currentTags.slice().sort();
                    const sortedTags = tags.slice().sort();
                    const areEqual =
                        sortedCurTags.length === sortedTags.length &&
                        sortedCurTags.every(
                            (value: string, index: number) =>
                                value === sortedTags[index]
                        );
                    const tagIds = map(tags, (tag: string) => parseInt(tag.split("##")[0], 10));
                    if (!areEqual) {
                        try {
                            const action = currentTags.length > tags.length ? TaggingActionTypes.UPDATE : TaggingActionTypes.INSERT;
                            const tagsToSend = action === TaggingActionTypes.UPDATE
                                ? map(currentTags.filter(tag => !tags.includes(tag)), (tag: string) => parseInt(tag.split("##")[0], 10))
                                : tagIds;
                            const response = await addEntityTags(
                                !isExternalTagging
                                    ? activeModule === SubModule.CAMPAIGN ? [recordId] : [{ sku: recordId, channel }]
                                    : [
                                          {
                                              sku: params.data.sku,
                                              channel: params.data.channel_id,
                                          },
                                      ],
                                updateMethod,
                                updatePath,
                                updateKey,
                                tagsToSend,
                                false,
                                action
                            );

                            if (!response.status) {
                                throw new Error(response.message);
                            }

                            const updatedTags = getGridTagsFormat(tagIds);
                            
                            setUpdateGridData({
                                params,
                                value: updatedTags,
                                isReduxState: true,
                            });

                            showToast(
                                "success",
                                `Updated tags for ${recordName}`
                            );
                        } catch (err: any) {
                            console.error(err.message);
                            showToast(
                                "danger",
                                `Unable to update tags for ${recordName}  ${err?.message && ": " + err?.message
                                }`
                            );
                        }
                    }
                }
            },
            [
                currentTags,
                tags,
                recordId,
                recordName,
                params,
                isExternalTagging,
                updateMethod,
                updatePath,
                updateKey,
            ]
        );

        const { recordType } =
            TAGS_MODULE_MAPPING[activeModule as keyof TagsMap];

            const avlTagClickHandler = useCallback(
                (tag: string) => {
                    setInputValue("");
                    if (tags.length === 10) {
                        showToast("danger", "Max tags limit reached.");
                        return;
                    }

                    const { tagType: newTagType } = getTagType(tag, recordType);
                    const hasSameType = tags.some((t) => {
                        const { tagType } = getTagType(t, recordType);
                        return tagType === newTagType && tagType !== "";
                    });
                    if (hasSameType) {
                        showToast("danger", "You cannot select more than one tag of the same type.");
                        return;
                    }
                    setTags((prev: string[]) => [...prev, tag]);
                    setAvlTags((prev: string[]) =>
                        prev.filter((avlTag) => avlTag !== tag)
                    );
                },
                [tags, recordType]
            );

        // Memoize the list item data
        const itemData = useMemo(
            () => ({
                recordType,
                onTagClick: avlTagClickHandler,
            }),
            [recordType, avlTagClickHandler]
        );

        // Memoize the popover content
        const popoverContent = useMemo(() => {
            return (
                <div className="shadow" style={{ borderRadius: "0.5rem" }}>
                    <div
                        className={`${isViewModule
                            ? "campaigns-tag-popover"
                            : "d-flex bg-white p-2"
                        }`}
                    >
                        <DynamicTagsComponent
                            {...{
                                tags,
                                setTags,
                                avlTagsConstant,
                                setAvlTags,
                                inputValue,
                                setInputValue,
                            }}
                            isEditable={isViewModule && canEditTags}
                            activeModule={activeModule}
                        />
                    </div>
                    {isViewModule && (
                        <>
                            <div
                                className={`avl-tags-container ${!canEditTags ? "pe-none opacity-50" : ""
                                }`}
                                style={{ maxHeight: "50vh", overflowY: "auto" }}
                            >
                                <div
                                    className="mb-2"
                                    style={{
                                        fontSize: "inherit",
                                        fontWeight: "600",
                                        color: "#9E9FA5",
                                    }}
                                >
                                    Select an option
                                </div>
                                {isProcessing ? (
                                    <div className="text-center p-3">
                                        <Spin size="small" />
                                    </div>
                                ) : avlTags.length ? (
                                    <List
                                        height={Math.min(
                                            filteredTags.length * 35,
                                            300
                                        )}
                                        itemCount={filteredTags.length}
                                        itemSize={35}
                                        width="100%"
                                        overscanCount={5}
                                        itemData={itemData}
                                    >
                                        {({ index, style, data }) => (
                                            <TagRow
                                                data={{
                                                    tag: filteredTags[index],
                                                    recordType: data.recordType,
                                                    onTagClick: data.onTagClick,
                                                }}
                                                style={style}
                                            />
                                        )}
                                    </List>
                                ) : (
                                    <span
                                        className="text-center fs-6"
                                        style={{
                                            color: "#B4B4B3",
                                            width: "100%",
                                        }}
                                    >
                                        {avlTagsConstant?.length
                                            ? "All available tags are selected"
                                            : "No existing tags available"}
                                    </span>
                                )}
                            </div>
                        </>
                    )}
                </div>
            );
        }, [
            isViewModule,
            tags,
            setTags,
            avlTagsConstant,
            setAvlTags,
            inputValue,
            setInputValue,
            canEditTags,
            activeModule,
            avlTags.length,
            filteredTags,
            itemData,
            isProcessing,
        ]);

        // Memoize the main content
        const mainContent = useMemo(
            () => (
                <div role="button">
                    <DynamicTagsComponent
                        {...{ tags, setTags, activeModule }}
                        isEditable={false}
                    />
                </div>
            ),
            [tags, setTags, activeModule]
        );

        return (
            <Popover
                placement="leftTop"
                content={popoverContent}
                trigger="click"
                onOpenChange={handleTagsPopoverVisibleChange}
            >
                {mainContent}
            </Popover>
        );
    }
);

export default CampaignTagsDropDown;
